/**
 * Composants partagés du fil d'actualité formateur.
 *
 * Fix 2026-09-02 : refonte "administrative Facebook" :
 * - Palette slate/blue (plus d'ambre crème)
 * - Cartes fond blanc, bordures discrètes, sans gradients ni ring
 * - Badge service_emetteur neutre (gris sur fond blanc)
 * - Modal publier sans emoji (charte)
 * - Avatars sobres (initiales sur fond slate)
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MessageCircle, Loader2, X, Trash2, Send, Image as ImageIcon, Pin,
  Calendar, MapPin, ExternalLink, GraduationCap, Briefcase,
  ThumbsUp, Heart, Award, Flame,
} from 'lucide-react';
import {
  useReagirActualiteMutation, useRetirerReactionActualiteMutation,
  useCommentairesActualiteQuery, useAjouterCommentaireMutation,
  useSupprimerCommentaireMutation, usePublierActualiteMutation,
  useSupprimerMaPublicationMutation, useReactionsActualiteQuery,
  type ActualiteEnrichieItem, type CommentaireItem, type ReactionAuteurItem,
} from '../../services/formateurApi';

// Fix 2026-09-07 : lightbox plein écran pour voir une image de publication.
// Fix 2026-09-07 (v2) : PAS de `document.body.style.overflow = hidden` —
// ça bloque le pull-to-refresh natif si un cleanup rate.
function ImageLightboxFormateur({ src, onClose }: { src: string; onClose: () => void }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[110] bg-black flex items-center justify-center"
      onClick={onClose}
      style={{ touchAction: 'pinch-zoom' }}
    >
      <button
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt=""
        className="max-w-full max-h-full object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>,
    document.body,
  );
}

const REACTIONS = [
  { key: 'like', icon: ThumbsUp, label: "J'aime" },
  { key: 'coeur', icon: Heart, label: 'Coeur' },
  { key: 'bravo', icon: Award, label: 'Bravo' },
  { key: 'feu', icon: Flame, label: 'Top' },
];

function fmtRelative(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "à l'instant";
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function initiales(nom: string): string {
  const parts = nom.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function CarteActualite({ a, onOpenComments }: {
  a: ActualiteEnrichieItem; onOpenComments: () => void;
}) {
  const [contentExpanded, setContentExpanded] = useState(false);
  const [reagir] = useReagirActualiteMutation();
  const [retirer] = useRetirerReactionActualiteMutation();
  const [supprimerPub] = useSupprimerMaPublicationMutation();
  const [showLikes, setShowLikes] = useState(false);
  const [showImage, setShowImage] = useState(false);

  const toggleLike = async () => {
    try {
      if (a.moi_a_reagi) await retirer(a.id).unwrap();
      else await reagir({ actualiteId: a.id, type_reaction: 'like' }).unwrap();
    } catch { /* silencieux */ }
  };

  const isMienne = a.auteur_role === 'formateur';
  const handleSupprimer = async () => {
    if (!window.confirm('Supprimer votre publication ?')) return;
    try { await supprimerPub(a.id).unwrap(); } catch { /* silencieux */ }
  };

  return (
    <article className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
      {/* Header auteur — style FB compact */}
      <header className="px-3 pt-3 pb-2 flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 text-slate-600 dark:text-gray-400 text-[11px] font-bold">
          {a.auteur_role === 'formateur' ? (
            <Briefcase size={14} />
          ) : a.auteur_role === 'etudiant' ? (
            <GraduationCap size={14} />
          ) : (
            initiales(a.auteur_nom || a.service_emetteur || '')
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {a.auteur_nom || a.service_emetteur || 'Anonyme'}
            </span>
            {/* Fix 2026-09-07 : classe de l'étudiant auteur (L1MI, L2EM…) */}
            {a.auteur_role === 'etudiant' && a.auteur_classe && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                {a.auteur_classe}
              </span>
            )}
            {a.epinglee && (
              <span className="inline-flex items-center gap-0.5 text-[9px] text-slate-500 dark:text-gray-500">
                <Pin size={9} /> Épinglé
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-gray-500 mt-0.5">
            {a.service_emetteur && (
              <>
                <span className="px-1.5 py-0.5 rounded border bg-slate-50 dark:bg-gray-800 text-slate-600 dark:text-gray-400 border-slate-200 dark:border-gray-700 uppercase tracking-wider text-[9px] font-semibold">
                  {a.service_emetteur}
                </span>
                <span>·</span>
              </>
            )}
            <span>{fmtRelative(a.date_publication)}</span>
          </div>
        </div>
        {isMienne && (
          <button onClick={handleSupprimer}
            className="p-1.5 text-slate-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 rounded transition">
            <Trash2 size={13} />
          </button>
        )}
      </header>

      {/* Contenu */}
      <div className="px-3 pb-2">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">{a.titre}</h3>
        {a.contenu && (() => {
          const LIMITE = 350;
          const trop_long = a.contenu.length > LIMITE;
          const affiche = contentExpanded || !trop_long
            ? a.contenu
            : a.contenu.slice(0, LIMITE).trimEnd() + '…';
          return (
            <>
              <p className="text-[13px] text-slate-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {affiche}
              </p>
              {trop_long && (
                <button
                  onClick={() => setContentExpanded(!contentExpanded)}
                  className="text-[12px] text-blue-600 dark:text-blue-400 font-medium hover:underline mt-1"
                >
                  {contentExpanded ? 'Voir moins' : 'Voir plus'}
                </button>
              )}
            </>
          );
        })()}

        {(a.date_evenement || a.lieu) && (
          <div className="text-[11px] text-slate-600 dark:text-gray-400 mt-2 flex flex-wrap items-center gap-2 bg-slate-50 dark:bg-gray-800/60 rounded px-2 py-1 border border-slate-200 dark:border-gray-700">
            {a.date_evenement && (
              <span className="inline-flex items-center gap-1">
                <Calendar size={10} />
                {new Date(a.date_evenement).toLocaleString('fr-FR', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            )}
            {a.lieu && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={10} /> {a.lieu}
              </span>
            )}
          </div>
        )}

        {a.lien_externe && (
          <a href={a.lien_externe} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[12px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-2 truncate max-w-full">
            <ExternalLink size={11} /> {a.lien_externe.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>

      {/* Image */}
      {a.image_url && (
        <>
          <button onClick={() => setShowImage(true)}
            className="block w-full">
            <img src={a.image_url} alt={a.titre}
              className="w-full max-h-96 object-cover bg-slate-100 dark:bg-gray-800 cursor-zoom-in" />
          </button>
          {showImage && <ImageLightboxFormateur src={a.image_url} onClose={() => setShowImage(false)} />}
        </>
      )}

      {/* Compteurs cliquables */}
      {(a.nb_reactions > 0 || a.nb_commentaires > 0) && (
        <div className="px-3 py-2 flex items-center justify-between text-[11px] text-slate-500 dark:text-gray-400 border-t border-slate-100 dark:border-gray-800">
          <button
            onClick={() => a.nb_reactions > 0 && setShowLikes(true)}
            disabled={a.nb_reactions === 0}
            className="inline-flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-300 disabled:opacity-50 transition">
            {a.nb_reactions > 0 && (
              <>
                <span className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                  <ThumbsUp size={9} className="text-white" fill="currentColor" />
                </span>
                <span>{a.nb_reactions}</span>
              </>
            )}
          </button>
          {a.nb_commentaires > 0 && (
            <button onClick={onOpenComments} className="hover:text-slate-800 dark:hover:text-gray-200 transition">
              {a.nb_commentaires} commentaire{a.nb_commentaires > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}

      {/* Actions — style Facebook (bordure grise, bouton neutre) */}
      <div className="flex items-center gap-1 px-2 py-1 border-t border-slate-100 dark:border-gray-800">
        <button onClick={toggleLike}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded text-xs font-semibold transition ${
            a.moi_a_reagi
              ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              : 'text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800'
          }`}>
          <ThumbsUp size={14} fill={a.moi_a_reagi ? 'currentColor' : 'none'} />
          J'aime
        </button>
        <button onClick={onOpenComments}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded text-xs font-semibold text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition">
          <MessageCircle size={14} />
          Commenter
        </button>
      </div>

      {showLikes && (
        <ModalReactions actualiteId={a.id} onClose={() => setShowLikes(false)} />
      )}
    </article>
  );
}

// ─── Modal "Qui a aimé" ─────────────────────────────────────────────────────

function ModalReactions({ actualiteId, onClose }: {
  actualiteId: number; onClose: () => void;
}) {
  const { data, isLoading } = useReactionsActualiteQuery(actualiteId);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white inline-flex items-center gap-2">
            <ThumbsUp size={14} className="text-blue-600 dark:text-blue-400" fill="currentColor" />
            Personnes qui ont réagi ({data?.total || 0})
          </h3>
          <button onClick={onClose} className="text-slate-400 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {isLoading && (
            <div className="text-center py-4 text-slate-500 dark:text-gray-500 text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
            </div>
          )}
          {(data?.results || []).map((r, i) => <LigneReaction key={i} r={r} />)}
          {!isLoading && (data?.results || []).length === 0 && (
            <div className="text-center py-6 text-slate-500 dark:text-gray-500 text-sm">
              Personne n'a encore réagi.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LigneReaction({ r }: { r: ReactionAuteurItem }) {
  const IconReact = REACTIONS.find(x => x.key === r.type_reaction)?.icon || ThumbsUp;
  return (
    <div className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-gray-800/60 rounded">
      {r.photo_url ? (
        <img src={r.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-gray-800 flex items-center justify-center text-slate-600 dark:text-gray-400">
          {r.type_auteur === 'formateur' ? <Briefcase size={14} /> : <GraduationCap size={14} />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 dark:text-white truncate">{r.nom_complet}</div>
        <div className="text-[10px] text-slate-500 dark:text-gray-500">
          {r.type_auteur === 'formateur' ? 'Formateur' : `Étudiant${r.classe ? ' · ' + r.classe : ''}`}
        </div>
      </div>
      <IconReact size={14} className="text-blue-600 dark:text-blue-400" fill="currentColor" />
    </div>
  );
}

// ─── Modal commentaires ────────────────────────────────────────────────────

export function ModalCommentaires({ actualiteId, onClose }: {
  actualiteId: number; onClose: () => void;
}) {
  const { data, isLoading } = useCommentairesActualiteQuery(actualiteId);
  const [contenu, setContenu] = useState('');
  const [replyTo, setReplyTo] = useState<CommentaireItem | null>(null);
  const [ajouter, { isLoading: ajoutLoading }] = useAjouterCommentaireMutation();
  const [supprimer] = useSupprimerCommentaireMutation();

  const racines = (data?.results || []).filter(c => !c.parent_id_commentaire);
  const reponses = (data?.results || []).filter(c => c.parent_id_commentaire);
  const reponsesByParent: Record<number, CommentaireItem[]> = {};
  for (const r of reponses) {
    (reponsesByParent[r.parent_id_commentaire!] ||= []).push(r);
  }

  const handleEnvoi = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contenu.trim()) return;
    try {
      await ajouter({
        actualiteId, contenu,
        parent_id_commentaire: replyTo?.id || null,
      }).unwrap();
      setContenu(''); setReplyTo(null);
    } catch { /* silencieux */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 rounded-t-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white inline-flex items-center gap-2">
            <MessageCircle size={14} className="text-blue-600 dark:text-blue-400" />
            Commentaires ({data?.total || 0})
          </h3>
          <button onClick={onClose} className="text-slate-400 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {isLoading && (
            <div className="text-center py-4 text-slate-500 dark:text-gray-500 text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
            </div>
          )}
          {racines.map(c => (
            <div key={c.id} className="space-y-2">
              <Commentaire c={c} onReply={() => setReplyTo(c)}
                onSupprimer={() => supprimer({ commentaireId: c.id, actualiteId })} />
              {(reponsesByParent[c.id] || []).map(r => (
                <div key={r.id} className="ml-8">
                  <Commentaire c={r}
                    onReply={() => setReplyTo(c)}
                    onSupprimer={() => supprimer({ commentaireId: r.id, actualiteId })} />
                </div>
              ))}
            </div>
          ))}
          {!isLoading && racines.length === 0 && (
            <div className="text-center py-6 text-slate-500 dark:text-gray-500 text-sm">
              Aucun commentaire pour l'instant. Soyez le premier à réagir.
            </div>
          )}
        </div>

        <form onSubmit={handleEnvoi}
          className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 p-3 space-y-2">
          {replyTo && (
            <div className="text-[10px] text-slate-500 dark:text-gray-400 flex items-center justify-between bg-slate-100 dark:bg-gray-800 rounded px-2 py-1">
              <span>Réponse à <strong>{replyTo.auteur_nom}</strong></span>
              <button type="button" onClick={() => setReplyTo(null)} className="text-slate-400 dark:text-gray-500 hover:text-slate-900 dark:hover:text-white">
                <X size={11} />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input value={contenu} onChange={(e) => setContenu(e.target.value)}
              placeholder={replyTo ? `Répondre à ${replyTo.auteur_nom}…` : 'Écrire un commentaire…'}
              maxLength={2000}
              className="flex-1 px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none" />
            <button type="submit" disabled={ajoutLoading || !contenu.trim()}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded transition">
              {ajoutLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Commentaire({ c, onReply, onSupprimer }: {
  c: CommentaireItem; onReply: () => void; onSupprimer: () => void;
}) {
  return (
    <div className="bg-slate-50 dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700 rounded-lg p-2.5">
      <div className="flex items-start justify-between gap-2 mb-0.5">
        <div className="flex items-center gap-1.5">
          <div className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-gray-300 uppercase tracking-wider">
            {c.auteur_nom || (c.auteur_type === 'etudiant' ? 'Étudiant' : 'Anonyme')}
          </div>
          <span className="text-[10px] text-slate-500 dark:text-gray-500">
            {fmtRelative(c.date_creation)}
          </span>
        </div>
        {c.a_moi && (
          <button onClick={onSupprimer}
            className="text-slate-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 p-0.5">
            <Trash2 size={10} />
          </button>
        )}
      </div>
      <p className="text-xs text-slate-700 dark:text-gray-200 whitespace-pre-wrap">{c.contenu}</p>
      {!c.parent_id_commentaire && (
        <button onClick={onReply}
          className="text-[10px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-1 font-semibold">
          Répondre
        </button>
      )}
    </div>
  );
}

// ─── Modal publication ──────────────────────────────────────────────────────

export function ModalPublier({ onClose }: { onClose: () => void }) {
  const [titre, setTitre] = useState('');
  const [contenu, setContenu] = useState('');
  const [type, setType] = useState('info');
  const [visEtu, setVisEtu] = useState(true);
  const [visParents, setVisParents] = useState(false);
  const [visForm, setVisForm] = useState(true);
  const [dateEvt, setDateEvt] = useState('');
  const [lieu, setLieu] = useState('');
  const [lien, setLien] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [erreur, setErreur] = useState('');
  const [publier, { isLoading }] = usePublierActualiteMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!titre.trim()) { setErreur('Titre requis'); return; }
    const fd = new FormData();
    fd.append('titre', titre);
    fd.append('contenu', contenu);
    fd.append('type', type);
    fd.append('visible_etudiants', String(visEtu));
    fd.append('visible_parents', String(visParents));
    fd.append('visible_formateurs', String(visForm));
    if (dateEvt) fd.append('date_evenement', dateEvt);
    if (lieu) fd.append('lieu', lieu);
    if (lien) fd.append('lien_externe', lien);
    if (image) fd.append('image', image);
    try {
      await publier(fd).unwrap();
      onClose();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur publication');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white">Publier une actualité</h3>
          <button type="button" onClick={onClose} className="text-slate-400 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Titre *</label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)}
              maxLength={200} required
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Contenu</label>
            <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
              rows={5} maxLength={5000}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100 resize-none focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100">
              <option value="info">Information</option>
              <option value="seminaire">Séminaire</option>
              <option value="cours">Cours / Programme</option>
              <option value="soutenance">Soutenance</option>
              <option value="excursion">Excursion</option>
              <option value="livre">Publication</option>
              <option value="projet">Projet</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Visible par</label>
            <div className="space-y-1">
              {([
                { l: 'Étudiants', v: visEtu, set: setVisEtu },
                { l: 'Parents', v: visParents, set: setVisParents },
                { l: 'Autres formateurs', v: visForm, set: setVisForm },
              ] as const).map(o => (
                <label key={o.l} className="flex items-center gap-2 text-xs text-slate-700 dark:text-gray-300 px-2 py-1 hover:bg-slate-100 dark:hover:bg-gray-800 rounded cursor-pointer">
                  <input type="checkbox" checked={o.v} onChange={(e) => o.set(e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                  {o.l}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Date évènement</label>
              <input type="datetime-local" value={dateEvt} onChange={(e) => setDateEvt(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Lieu</label>
              <input value={lieu} onChange={(e) => setLieu(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Lien externe (optionnel)</label>
            <input type="url" value={lien} onChange={(e) => setLien(e.target.value)}
              placeholder="https://…"
              className="w-full px-2 py-1.5 text-xs bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1 inline-flex items-center gap-1">
              <ImageIcon size={11} /> Image (optionnel)
            </label>
            <input type="file" accept="image/*"
              onChange={(e) => setImage(e.target.files?.[0] || null)}
              className="w-full text-xs text-slate-500 dark:text-gray-400 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100 dark:file:bg-gray-700 file:text-slate-700 dark:file:text-gray-200" />
          </div>

          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 rounded p-2 text-xs">
              {erreur}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !titre.trim()}
            className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Publier
          </button>
        </footer>
      </form>
    </div>
  );
}
